import {
  javascript,
  javascriptLanguage,
  scopeCompletionSource,
} from "@codemirror/lang-javascript";
import { EditorView } from "@codemirror/view";
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { tags as t } from "@lezer/highlight";
import type { ReactCodeMirrorProps } from "@uiw/react-codemirror";

export const EDITOR_BG = "#efefef";
export const EDITOR_HEIGHT = "280px";
export const EDITOR_PLACEHOLDER = "// Type some code ->";

const FONT_FAMILY = '"Fira Code", "Fira Mono", "Cascadia Code", monospace';

// Token colors matching the design (Atom One Light palette).
const highlightStyle = HighlightStyle.define([
  { tag: t.comment, color: "#a0a1a7", fontStyle: "italic" },
  { tag: [t.keyword, t.modifier, t.controlKeyword], color: "#e06c5d" },
  { tag: [t.operatorKeyword, t.bool, t.null], color: "#e06c5d" },
  { tag: [t.string, t.special(t.string)], color: "#d99a5e" },
  { tag: [t.number, t.regexp], color: "#a679c2" },
  {
    tag: [t.function(t.variableName), t.function(t.propertyName)],
    color: "#4ca7d4",
  },
  { tag: [t.variableName, t.propertyName], color: "#4ca7d4" },
  { tag: [t.className, t.typeName], color: "#4ca7d4" },
  { tag: [t.operator, t.punctuation, t.separator, t.bracket], color: "#888888" },
]);

// Editor-chrome theme (background, gutter, cursor, selection).
const lightTheme = EditorView.theme(
  {
    "&": {
      background: EDITOR_BG,
      color: "#383a42",
      fontSize: "13px",
    },
    // The text surface must be colored too, otherwise it stays white.
    ".cm-scroller": {
      background: EDITOR_BG,
      lineHeight: "1.6",
    },
    ".cm-content": {
      fontFamily: FONT_FAMILY,
      padding: "14px 0",
    },
    ".cm-gutters": {
      background: EDITOR_BG,
      color: "#bcbcbc",
      border: "none",
    },
    ".cm-activeLineGutter, .cm-activeLine": {
      background: "transparent",
    },
    ".cm-cursor": {
      borderLeftColor: "#526fff",
    },
    "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground":
      {
        background: "#b3c5e6 !important",
      },
    "::selection": {
      background: "#b3c5e6",
    },
  },
  { dark: false }
);

export const editorExtensions = [
  javascript(),
  javascriptLanguage.data.of({
    autocomplete: scopeCompletionSource(globalThis),
  }),
  lightTheme,
  syntaxHighlighting(highlightStyle),
  EditorView.lineWrapping,
];

export const editorBasicSetup: ReactCodeMirrorProps["basicSetup"] = {
  lineNumbers: true,
  foldGutter: false,
  highlightActiveLine: false,
  highlightActiveLineGutter: false,
  autocompletion: true,
  completionKeymap: true,
  searchKeymap: false,
};
